import React, { createContext, FC, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import {
    AppMode,
    useAppMode,
    useCommentArgumentsQuery,
    useSelectedComment,
    useTicketOfflineQuery,
} from '@tymely/services';
import { getVisibleArgs, IArgument, requiresInput } from '@tymely/atoms';
import { MD_ID_INTENT, STEP_ARGUMENTS, STEP_INTENT, STEP_RESOLUTION } from '@tymely/features/Ticket/QaMode/constants';
import { QaStepperContextProps } from '@tymely/features/Ticket/QaMode/types';
import { useAtomValue } from 'jotai';
import { selectedPolicySetIdAtom } from '@tymely/atoms/policySet.atoms';

const QaStepperContext = createContext<QaStepperContextProps | undefined>(undefined);

export const QaStepperProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const [activeStep, setActiveStep] = useState<number>(-1);
    const [isIntentChanged, setIsIntentChanged] = useState<boolean>(false);
    const [hasOneRejectedArg, setHasOneRejectedArg] = useState<boolean>(false);
    const { data: ticket } = useTicketOfflineQuery(undefined, false);
    const { navigateTicket } = useAppMode();
    const selectedComment = useSelectedComment();
    const selectedPolicySetId = useAtomValue(selectedPolicySetIdAtom);
    const {
        data: args,
        isLoading: isArgQueryLoading,
        isFetching: isArgQueryFetching,
        refetch: refetchArgs,
        dataUpdatedAt,
    } = useCommentArgumentsQuery(selectedComment?.id || undefined, undefined, true, true, selectedPolicySetId);
    const { data: prodArgs } = useCommentArgumentsQuery(
        selectedComment?.id || undefined,
        undefined,
        true,
        false,
        selectedPolicySetId,
    );
    const [intentArg, setIntentArg] = useState<IArgument | undefined>(undefined);
    const [originalIntentArg, setOriginalIntentArg] = useState<IArgument | undefined>(undefined);
    const [approvedArgs, setApprovedArgs] = useState<Set<IArgument['md_id']>>(new Set());
    const textArgs = useMemo(
        () => args && getVisibleArgs(args.filter((arg) => arg.arg_type === 'TEXT_ARGUMENT' && !requiresInput(arg))),
        [args],
    );

    useEffect(() => {
        if (ticket) {
            setHasOneRejectedArg(false);
            setIsIntentChanged(false);
            setActiveStep(STEP_INTENT);
            setApprovedArgs(new Set());
            navigateTicket(ticket.id, AppMode.QA, true /* replace */);
        }
    }, [ticket]);

    useEffect(() => {
        if (prodArgs) {
            setOriginalIntentArg(prodArgs?.find(({ md_id }) => md_id === MD_ID_INTENT));
        }
    }, [prodArgs]);

    useEffect(() => {
        if (args) {
            const updatedApprovedArgs = new Set(
                (textArgs ?? []).filter((arg) => arg?.qa_at && !arg?.deleted_at).map((arg) => arg.md_id),
            );
            setApprovedArgs(updatedApprovedArgs);

            const _intentArg = args.find(({ md_id }) => md_id === MD_ID_INTENT);
            const intentIsApproved = _intentArg?.qa_at && !_intentArg?.deleted_at;
            const hasOneRejectedArg = (args ?? []).find((arg) => arg?.qa_at && arg?.deleted_at);

            if (updatedApprovedArgs.size === textArgs?.length || hasOneRejectedArg) {
                setActiveStep(STEP_RESOLUTION);
                if (hasOneRejectedArg) {
                    setIsIntentChanged(hasOneRejectedArg.md_id === MD_ID_INTENT);
                    setHasOneRejectedArg(hasOneRejectedArg.md_id !== MD_ID_INTENT);
                }
            } else if (intentIsApproved || !_intentArg || selectedPolicySetId) {
                setIsIntentChanged(false);
                setActiveStep(STEP_ARGUMENTS);
            } else {
                setActiveStep(STEP_INTENT);
            }

            setIntentArg(_intentArg);
        }
    }, [dataUpdatedAt, textArgs]);

    return (
        <QaStepperContext.Provider
            value={{
                args,
                textArgs,
                intentArg,
                activeStep,
                refetchArgs,
                approvedArgs,
                originalArgs: prodArgs,
                setIntentArg,
                setActiveStep,
                isIntentChanged,
                setApprovedArgs,
                isArgQueryLoading: isArgQueryLoading || isArgQueryFetching,
                hasOneRejectedArg,
                originalIntentArg,
                setIsIntentChanged,
            }}
        >
            {children}
        </QaStepperContext.Provider>
    );
};

export const useQaStepperContext = (): QaStepperContextProps => {
    const context = useContext(QaStepperContext);
    if (!context) {
        throw new Error('useQaStepperContext must be used within a QaStepperProvider');
    }
    return context;
};
